'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CityCombobox from '@/components/ui/CityCombobox';
import axios from 'axios';
import useLanguageChange from '@/hooks/useLanguageChange';
import useDebounce from '@/hooks/useDebounce';
import { env } from '@/constant/configs';
import { useRouter } from 'next/navigation';
import { useOnlineShopTotalCount } from '@/stores/useOnlineShopTotalCount';

interface Category {
  id: string;
  label: string;
  group?: string;
}

type CityDropdownProps = {
  handleMenuClose?: () => void;
};

const CityDropdown = ({ handleMenuClose }: CityDropdownProps) => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery);
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { localeCode } = useLanguageChange();

  const { setTotalOnlineShop } = useOnlineShopTotalCount();

  const handleSearch = useCallback(
    async (query: string) => {
      if (categories.length === 0) {
        setIsLoading(true);
      }

      try {
        const response = await axios.get(env.api_url + '/v1/customer/cities', {
          params: {
            name: query,
            is_favourite: 0,
            type: 'restaurant',
            order_by: 'order_index',
            order_dir: 'asc',
            per_page: 10,
          },
          headers: {
            'Accept-Language': localeCode,
          },
        });
        const resData = await response?.data;
        const data = resData?.data;
        if (resData?.data) {
          type itemType = (typeof data)[0];
          const formattedData = data?.map((item: itemType) => ({
            id: item.slug,
            label: item.name,
            itemId: item.id,
          }));
          setCategories(formattedData);
        }
        if (resData?.online_shop) {
          setTotalOnlineShop(resData.online_shop?.restaurants_count ?? 0);
        }
      } catch (error) {
        if (categories.length === 0) {
          setCategories([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [categories.length, localeCode, setTotalOnlineShop]
  );

  useEffect(() => {
    handleSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, handleSearch]);

  const handleSelect = (id: string) => {
    if (id) {
      router.push(`/city/${id}`);
    }
  };

  return (
    <div className="relative" tabIndex={1}>
      <CityCombobox
        categories={categories}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSelect={handleSelect}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CityDropdown;
